/**
 * NotFound — branded 404 page wired into __root.tsx as the router's
 * `notFoundComponent`. Replaces TanStack Router's default unstyled
 * fallback so a customer who lands on a stale link or a typo'd URL
 * sees the same fiduciary-grade marketing chrome as the rest of the
 * site, with a clear path back into the product.
 *
 * Content design (Task 3.20):
 *   - Hero copy that reads in plain English; never "Error 404", never
 *     a stack trace, never the word "page".
 *   - Three quick links: home, app dashboard, contact. We always show
 *     all three — the app dashboard link route-guards itself on
 *     `/app` and will redirect to `/auth/login` if the visitor isn't
 *     signed in, which is the right behaviour anyway.
 *   - "Report this URL" CTA opens the user's mail client with the
 *     attempted path pre-filled. This is the entry-point for support
 *     to learn about broken inbound links from email campaigns,
 *     partner sites, etc.
 *
 * Accessibility: section has a single `<h1>`, all interactive elements
 * are focusable links/buttons with visible text labels, no colour-only
 * affordances.
 */

import { Link } from '@tanstack/react-router';
import { MarketingLayout } from '../layouts/marketing-layout';

export interface NotFoundProps {
  /** Optional path that 404'd — defaults to `window.location.pathname`
   *  at render time. Surfaced verbatim in the hero so the user can see
   *  what they typed (or what was linked to them). */
  attemptedPath?: string;
}

export function NotFound({ attemptedPath }: NotFoundProps = {}) {
  // window.location is safe — TanStack Router only renders this
  // component browser-side. Default-arg keeps SSR-style call sites
  // (e.g. unit tests that pass `attemptedPath` explicitly) clean.
  const path =
    attemptedPath ??
    (typeof window !== 'undefined' ? window.location.pathname : '/');

  return (
    <MarketingLayout>
      <section
        aria-labelledby="not-found-title"
        style={{
          maxWidth: 720,
          margin: '0 auto',
          padding: '96px 48px 120px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            background: 'var(--surface)',
            border: '1px solid var(--hairline)',
            borderRadius: 'var(--r-xl)',
            padding: '56px 48px 48px',
            width: '100%',
            maxWidth: 600,
          }}
        >
          <p className="eyebrow" style={{ marginBottom: 24 }}>
            404 · Not found
          </p>

          <h1
            id="not-found-title"
            className="serif"
            style={{
              fontSize: 'clamp(36px, 6vw, 60px)',
              fontStyle: 'italic',
              fontWeight: 300,
              color: 'var(--ink)',
              lineHeight: 1.1,
              letterSpacing: '-0.025em',
              margin: '0 0 24px',
            }}
          >
            We can&rsquo;t find that page
          </h1>

          <p
            style={{
              fontSize: 17,
              color: 'var(--ink-3)',
              lineHeight: 1.6,
              margin: '0 0 12px',
            }}
          >
            The link you followed may be out of date, mistyped, or refer to a
            page that has moved. Your data is safe. Nothing has changed.
          </p>

          {/* Show the attempted path so support tickets carry a real value. */}
          <p
            className="mono"
            data-testid="attempted-path"
            style={{
              fontSize: 13,
              color: 'var(--ink-4)',
              wordBreak: 'break-all',
              margin: '0 0 40px',
            }}
          >
            Tried:&nbsp;{path}
          </p>

          <div
            style={{
              borderTop: '1px solid var(--hairline)',
              marginBottom: 32,
            }}
          />

          {/* Quick links — three routes that always make sense:
              home, the signed-in dashboard, and contact. */}
          <div
            style={{
              display: 'flex',
              gap: 16,
              flexWrap: 'wrap',
              justifyContent: 'center',
              alignItems: 'center',
              marginBottom: 24,
            }}
          >
            <Link
              to="/"
              style={{
                background: 'var(--accent-400)',
                color: 'var(--primary-900)',
                padding: '11px 22px',
                borderRadius: 10,
                fontSize: 15,
                fontWeight: 600,
                textDecoration: 'none',
              }}
            >
              Return home
            </Link>

            <Link
              to={'/app' as never}
              style={{
                fontSize: 15,
                color: 'var(--primary-600)',
                textDecoration: 'none',
                borderBottom: '1px solid rgba(31,77,69,0.25)',
                paddingBottom: 1,
              }}
            >
              Go to dashboard
            </Link>

            <Link
              to="/contact"
              style={{
                fontSize: 15,
                color: 'var(--primary-600)',
                textDecoration: 'none',
                borderBottom: '1px solid rgba(31,77,69,0.25)',
                paddingBottom: 1,
              }}
            >
              Contact us
            </Link>
          </div>

          {/* Broken inbound link guidance. Neutral wording only — this page
              can render before tenant branding loads, so no firm details and
              no Elreign contact address are surfaced here. */}
          <p
            data-testid="report-url-cta"
            style={{ fontSize: 13, color: 'var(--ink-4)', margin: 0, lineHeight: 1.6 }}
          >
            Followed a broken link from somewhere else? Please let your
            administrator know so it can be fixed at the source.
          </p>
        </div>
      </section>
    </MarketingLayout>
  );
}
